import { type ICommentListItem } from '@/types/IComment';
import { type IErrorData } from '@/utils/getErrorData';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  createOrderCommentThunk,
  getCommentLinksThunk,
  getOrderCommentsThunk
} from './commentsThuks';
import {
  type ICommentLinks,
  type CommentLinksMap
} from '@/hooks/useCommentLinks';

export interface ICommentsState {
  commentsByOrder: Record<string, ICommentListItem[]>;
  metaByOrder: Record<string, { total: number }>;
  isLoadingByOrder: Record<string, boolean>;
  errorByOrder: Record<string, IErrorData | null>;
  hasMoreByOrder: Record<string, boolean>;
  isCreatingByOrder: Record<string, boolean>;
  createErrorByOrder: Record<string, IErrorData | null>;
  linksMapByOrder: Record<string, CommentLinksMap>;
}

const initialState: ICommentsState = {
  commentsByOrder: {},
  metaByOrder: {},
  isLoadingByOrder: {},
  errorByOrder: {},
  hasMoreByOrder: {},
  isCreatingByOrder: {},
  createErrorByOrder: {},
  linksMapByOrder: {}
};

const comments = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    resetComments (state, action: PayloadAction<{ orderUuid: string }>) {
      const { orderUuid } = action.payload;
      state.commentsByOrder[orderUuid] = [];
      state.metaByOrder[orderUuid] = { total: 0 };
      state.isLoadingByOrder[orderUuid] = false;
      state.errorByOrder[orderUuid] = null;
      state.hasMoreByOrder[orderUuid] = true;
      state.isCreatingByOrder[orderUuid] = false;
      state.createErrorByOrder[orderUuid] = null;
    },
    setCommentLinks (
      state,
      action: PayloadAction<{
        orderUuid: string;
        commentId: string;
        links: ICommentLinks;
      }>
    ) {
      const { orderUuid, commentId, links } = action.payload;
      if (typeof state.linksMapByOrder[orderUuid] === 'undefined') {
        state.linksMapByOrder[orderUuid] = {};
      }
      state.linksMapByOrder[orderUuid][commentId] = links;
    }
  },
  extraReducers: (builder) => {
    // Получение комментариев
    builder.addCase(getOrderCommentsThunk.pending, (state, action) => {
      const orderUuid = action.meta.arg.orderUuid;
      state.isLoadingByOrder[orderUuid] = true;
      state.errorByOrder[orderUuid] = null;
    });
    builder.addCase(getOrderCommentsThunk.fulfilled, (state, action) => {
      const { orderUuid, append } = action.meta.arg;
      const { items, meta } = action.payload;
      if (append === true) {
        state.commentsByOrder[orderUuid] = [
          ...(state.commentsByOrder[orderUuid] ?? []),
          ...items
        ];
      } else {
        state.commentsByOrder[orderUuid] = items;
      }
      state.metaByOrder[orderUuid] = meta;
      state.isLoadingByOrder[orderUuid] = false;
      state.errorByOrder[orderUuid] = null;
      state.hasMoreByOrder[orderUuid] =
        (state.commentsByOrder[orderUuid]?.length ?? 0) < meta.total;
    });
    builder.addCase(getOrderCommentsThunk.rejected, (state, action) => {
      const orderUuid = action.meta.arg.orderUuid;
      state.isLoadingByOrder[orderUuid] = false;
      state.errorByOrder[orderUuid] = action.payload ?? {
        message: 'Ошибка загрузки комментариев'
      };
    });
    // Создание комментария
    builder.addCase(createOrderCommentThunk.pending, (state, action) => {
      const orderUuid = action.meta.arg.order;
      state.isCreatingByOrder[orderUuid] = true;
      state.createErrorByOrder[orderUuid] = null;
    });
    builder.addCase(createOrderCommentThunk.fulfilled, (state, action) => {
      const orderUuid = action.meta.arg.order;
      state.commentsByOrder[orderUuid] = [
        action.payload,
        ...(state.commentsByOrder[orderUuid] ?? [])
      ];
      if (typeof state.metaByOrder[orderUuid] !== 'undefined') {
        state.metaByOrder[orderUuid].total += 1;
      } else {
        state.metaByOrder[orderUuid] = { total: 1 };
      }
      state.isCreatingByOrder[orderUuid] = false;
      state.createErrorByOrder[orderUuid] = null;
    });
    builder.addCase(createOrderCommentThunk.rejected, (state, action) => {
      const orderUuid = action.meta.arg.order;
      state.isCreatingByOrder[orderUuid] = false;
      state.createErrorByOrder[orderUuid] = action.payload ?? {
        message: 'Ошибка создания комментария'
      };
    });
    builder.addCase(getCommentLinksThunk.fulfilled, (state, action) => {
      const { orderUuid, commentId, links } = action.payload;
      if (typeof state.linksMapByOrder[orderUuid] === 'undefined') {
        state.linksMapByOrder[orderUuid] = {};
      }
      state.linksMapByOrder[orderUuid][commentId] = {
        images: {
          ...(state.linksMapByOrder[orderUuid][commentId]?.images ?? {}),
          ...links.images
        },
        files: {
          ...(state.linksMapByOrder[orderUuid][commentId]?.files ?? {}),
          ...links.files
        }
      };
    });
  }
});

export const { resetComments } = comments.actions;
export default comments.reducer;
